import React, { createContext, useContext, useMemo } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
  DeviceStatusProfileOverrides,
  DeviceStatusRefreshInterval,
  Scope,
  TimeRange,
} from './types';
import { DEFAULT_DEVICE_STATUS_REFRESH_INTERVAL } from './queryOptions';

// Shared state for the Device Status tool. Scope + range live in the URL query
// string so the tiles page and the Timeline subpage stay in sync and every view
// is shareable/bookmarkable (and survives a refresh on the subpage). The applied
// Advanced-settings config is kept in memory only (it is per-request free text,
// too large for the URL); when unset, the backend falls back to its defaults.

// Zones deselected by default: the Test platform is excluded until the user opts
// back in, matching the request to keep it off out of the box.
export const DEFAULT_HIDDEN_ZONES = ['Test platform'];

const REFRESH_INTERVAL_STORAGE_KEY = 'remus.deviceStatus.refreshIntervalMs';
const REFRESH_INTERVALS: DeviceStatusRefreshInterval[] = [null, 30_000, 60_000, 120_000, 300_000];

type DeviceStatusContextValue = {
  scope?: Scope;
  setScope: (scope: Scope | undefined) => void;
  range: TimeRange;
  setRange: (range: TimeRange) => void;
  config?: DeviceStatusProfileOverrides;
  setConfig: (config: DeviceStatusProfileOverrides | undefined) => void;
  onlyErrors: boolean;
  setOnlyErrors: (value: boolean) => void;
  hiddenZones: string[];
  setHiddenZones: (zones: string[]) => void;
  faultLevels: string[];
  setFaultLevels: (values: string[]) => void;
  eventUsernames: string[];
  setEventUsernames: (values: string[]) => void;
  modes: string[];
  setModes: (values: string[]) => void;
  eventTypes: string[];
  setEventTypes: (values: string[]) => void;
  refreshIntervalMs: DeviceStatusRefreshInterval;
  setRefreshIntervalMs: (value: DeviceStatusRefreshInterval) => void;
};

const DeviceStatusContext = createContext<DeviceStatusContextValue | null>(null);

export function useDeviceStatusContext(): DeviceStatusContextValue {
  const ctx = useContext(DeviceStatusContext);
  if (!ctx) {
    throw new Error('useDeviceStatusContext must be used within DeviceStatusProvider');
  }
  return ctx;
}

function parseIds(params: URLSearchParams, key: string): number[] {
  return (params.get(key) ?? '')
    .split(',')
    .map(Number)
    .filter(id => Number.isFinite(id) && id > 0);
}

// The filters are independent and combine with AND; a param is only present in
// the URL when its filter has values. No param at all means "no scope yet" so
// the page can seed its default.
function parseScope(params: URLSearchParams): Scope | undefined {
  const groupIds = parseIds(params, 'groupIds');
  const deviceTypes = (params.get('deviceTypes') ?? '').split(',').filter(Boolean);
  const applicationIds = parseIds(params, 'applicationIds');
  if (!groupIds.length && !deviceTypes.length && !applicationIds.length) {
    return undefined;
  }
  return {
    groupIds: groupIds.length ? groupIds : undefined,
    deviceTypes: deviceTypes.length ? deviceTypes : undefined,
    applicationIds: applicationIds.length ? applicationIds : undefined,
  };
}

function parseRange(params: URLSearchParams): TimeRange {
  const from = params.get('from');
  const to = params.get('to');
  const presetKey = params.get('rangePreset') ?? undefined;
  const storedMode = params.get('rangeMode');
  const mode =
    storedMode === 'quick' || storedMode === 'custom'
      ? storedMode
      : presetKey
      ? 'quick'
      : from && to
      ? 'custom'
      : undefined;
  return {
    from: from ? moment(from) : undefined,
    to: to ? moment(to) : undefined,
    presetKey,
    mode,
  };
}

function parseOnlyErrors(params: URLSearchParams): boolean {
  return params.get('errors') === '1';
}

function parseHiddenZones(params: URLSearchParams): string[] {
  const raw = params.get('hiddenZones');
  if (raw === null) {
    return DEFAULT_HIDDEN_ZONES;
  }
  if (raw === '') {
    return [];
  }
  return raw.split(',');
}

function parseRepeatedValues(params: URLSearchParams, key: string): string[] {
  return [
    ...new Set(
      params
        .getAll(key)
        .map(value => value.trim())
        .filter(Boolean)
    ),
  ];
}

function sameZones(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

// The default comes from an untyped JS module; pin it to the union type here.
const DEFAULT_REFRESH_INTERVAL =
  DEFAULT_DEVICE_STATUS_REFRESH_INTERVAL as DeviceStatusRefreshInterval;

function storedRefreshInterval(): DeviceStatusRefreshInterval {
  if (typeof window === 'undefined') {
    return DEFAULT_REFRESH_INTERVAL;
  }
  const stored = window.localStorage.getItem(REFRESH_INTERVAL_STORAGE_KEY);
  if (stored === 'off') {
    return null;
  }
  const parsed = Number(stored);
  return REFRESH_INTERVALS.includes(parsed as DeviceStatusRefreshInterval)
    ? (parsed as DeviceStatusRefreshInterval)
    : DEFAULT_REFRESH_INTERVAL;
}

const DeviceStatusProvider = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = React.useRef(searchParams);
  searchParamsRef.current = searchParams;
  // Applied config lives only in memory. This provider is the layout element for
  // both the tiles page and the Timeline subpage, so it stays mounted across the
  // drill-down and the config survives without a global store.
  const [config, setConfigState] = React.useState<DeviceStatusProfileOverrides | undefined>();
  const [refreshIntervalMs, setRefreshIntervalState] =
    React.useState<DeviceStatusRefreshInterval>(storedRefreshInterval);

  const scope = useMemo(() => parseScope(searchParams), [searchParams]);
  const range = useMemo(() => parseRange(searchParams), [searchParams]);
  const onlyErrors = useMemo(() => parseOnlyErrors(searchParams), [searchParams]);
  const hiddenZones = useMemo(() => parseHiddenZones(searchParams), [searchParams]);
  const faultLevels = useMemo(
    () => parseRepeatedValues(searchParams, 'faultLevel'),
    [searchParams]
  );
  const eventUsernames = useMemo(
    () => parseRepeatedValues(searchParams, 'eventUsername'),
    [searchParams]
  );
  // "mode" is taken by the legacy exclusive-scope param, hence "workMode".
  const modes = useMemo(() => parseRepeatedValues(searchParams, 'workMode'), [searchParams]);
  const eventTypes = useMemo(() => parseRepeatedValues(searchParams, 'eventType'), [searchParams]);

  // Migrate links from the removed Statistics-local filters: sfEventType/sfMode
  // become the shared params, device/zone/level locals are dropped (the shared
  // filters cover them). Runs once per arrival URL.
  React.useEffect(() => {
    const params = searchParamsRef.current;
    const legacyKeys = ['sfDevice', 'sfZone', 'sfLevel', 'sfMode', 'sfEventType'];
    if (!legacyKeys.some(key => params.has(key))) {
      return;
    }
    const next = new URLSearchParams(params);
    if (!next.has('eventType')) {
      params.getAll('sfEventType').forEach(value => next.append('eventType', value));
    }
    if (!next.has('workMode')) {
      params.getAll('sfMode').forEach(value => next.append('workMode', value));
    }
    legacyKeys.forEach(key => next.delete(key));
    searchParamsRef.current = next;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<DeviceStatusContextValue>(() => {
    const mutateParams = (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParamsRef.current);
      next.delete('connected');
      mutate(next);
      searchParamsRef.current = next;
      setSearchParams(next, { replace: true });
    };

    return {
      scope,
      setScope: nextScope => {
        mutateParams(next => {
          // `mode` is a leftover of the old exclusive group-XOR-type scope.
          next.delete('mode');
          next.delete('groupIds');
          next.delete('deviceTypes');
          next.delete('applicationIds');
          if (nextScope?.groupIds?.length) {
            next.set('groupIds', nextScope.groupIds.join(','));
          }
          if (nextScope?.deviceTypes?.length) {
            next.set('deviceTypes', nextScope.deviceTypes.join(','));
          }
          if (nextScope?.applicationIds?.length) {
            next.set('applicationIds', nextScope.applicationIds.join(','));
          }
        });
      },
      range,
      setRange: nextRange => {
        mutateParams(next => {
          if (nextRange.from) {
            next.set('from', nextRange.from.toISOString());
          } else {
            next.delete('from');
          }
          if (nextRange.to) {
            next.set('to', nextRange.to.toISOString());
          } else {
            next.delete('to');
          }
          if (nextRange.presetKey) {
            next.set('rangePreset', nextRange.presetKey);
          } else {
            next.delete('rangePreset');
          }
          const mode =
            nextRange.mode ??
            (nextRange.presetKey ? 'quick' : nextRange.from && nextRange.to ? 'custom' : undefined);
          if (mode) {
            next.set('rangeMode', mode);
          } else {
            next.delete('rangeMode');
          }
        });
      },
      config,
      setConfig: setConfigState,
      onlyErrors,
      setOnlyErrors: value => {
        mutateParams(next => {
          if (value) {
            next.set('errors', '1');
          } else {
            next.delete('errors');
          }
        });
      },
      hiddenZones,
      setHiddenZones: zones => {
        mutateParams(next => {
          if (sameZones(zones, DEFAULT_HIDDEN_ZONES)) {
            next.delete('hiddenZones');
          } else {
            next.set('hiddenZones', zones.join(','));
          }
        });
      },
      faultLevels,
      setFaultLevels: values => {
        mutateParams(next => {
          next.delete('faultLevel');
          values.forEach(value => next.append('faultLevel', value));
        });
      },
      eventUsernames,
      setEventUsernames: values => {
        mutateParams(next => {
          next.delete('eventUsername');
          values.forEach(value => next.append('eventUsername', value));
        });
      },
      modes,
      setModes: values => {
        mutateParams(next => {
          next.delete('workMode');
          values.forEach(value => next.append('workMode', value));
        });
      },
      eventTypes,
      setEventTypes: values => {
        mutateParams(next => {
          next.delete('eventType');
          values.forEach(value => next.append('eventType', value));
        });
      },
      refreshIntervalMs,
      setRefreshIntervalMs: value => {
        setRefreshIntervalState(value);
        window.localStorage.setItem(
          REFRESH_INTERVAL_STORAGE_KEY,
          value == null ? 'off' : String(value)
        );
      },
    };
  }, [
    scope,
    range,
    config,
    onlyErrors,
    hiddenZones,
    faultLevels,
    eventUsernames,
    modes,
    eventTypes,
    refreshIntervalMs,
    setSearchParams,
  ]);

  return (
    <DeviceStatusContext.Provider value={value}>
      <Outlet />
    </DeviceStatusContext.Provider>
  );
};

export default DeviceStatusProvider;
